<template>
  <FormGroup>
    <FormGroup>
      <FormField label="Address" :error="form.fieldErrors('location')">
        <AutocompleteAddress v-model="form.fields.location" />
      </FormField>

      <Map :coordinates="coordinates!" />

      <!-- A building is never listed for sale; the warning is a unit's -->
      <Hint v-if="!forBuilding" icon="info-circle">
        <View direction="column" gap="sm">
          <div>
            Make sure you have not listed this property for sale as For Rent listings cannot display simultaneously with
            a For Sale on Property platforms.
          </div>
          <div>
            <Link color="accent" target="_blank" :to="advertisingForSaleandForRentListingsSimultaneouslyLink">
              Learn more
            </Link>
          </div>
        </View>
      </Hint>
    </FormGroup>

    <!-- The creation flow puts the Portfolio field here; the edit dialog leaves it empty -->
    <slot name="after-address" />

    <FormGroup>
      <FormField label="Hide address on platforms" direction="row">
        <Switch v-model="form.fields.isAddressHidden" data-testid="is-address-hidden-switch" />
      </FormField>

      <Text v-if="forBuilding" variant="body-medium" color="secondary">
        Every unit is published on property platforms such as Zillow. With this on, we only share the building's
        approximate location and show the address once a renter requests a showing. We do not recommend this.
      </Text>
      <Text v-else variant="body-medium" color="secondary">
        Your listing will be published on Property platforms such Zillow. When this switch is on, we’ll only share your
        approximate location and show the address once the Renter requests for a Showing. We do not recommend this.
      </Text>
    </FormGroup>

    <Text v-if="forBuilding" variant="body-small" color="secondary">Every unit's address follows the building's.</Text>
  </FormGroup>
</template>

<script setup lang="ts">
import { helpers, required } from '@vuelidate/validators';
import { ref, watch } from 'vue';

import { FormField, FormGroup, Hint, Link, Switch, Text, View, useForm } from '@tallo/design-system';
import { advertisingForSaleandForRentListingsSimultaneouslyLink } from '@tallo/navigation';
import { AddressDto, Map, PropertyLocationDto } from '@tallo/property';

import { useAddressLookup } from '../../composables/address-lookup';
import { useDetailsHolder } from '../../composables/details-holder';
import AutocompleteAddress from '../autocomplete-address.vue';

const holder = useDetailsHolder();
/** A building's address is every unit's: the copy says so, and nothing about listing for sale */
const forBuilding = holder.kind === 'building';

const form = useForm<{ location: AddressDto | null; isAddressHidden: boolean }>(
  { location: null, isAddressHidden: false },
  { location: { required: helpers.withMessage('Address is required', required) } },
);

/** The address as it is on record; it needs no lookup, only a freshly picked one does */
const onRecord = ref<PropertyLocationDto>();
const { coordinates, isLoading } = useAddressLookup(() =>
  form.fields.location === onRecord.value ? null : form.fields.location,
);

/** `portfolioId` files a unit being created; the edit dialog never passes one */
async function save(portfolioId?: string) {
  return form.submit((fields) =>
    holder.saveLocation(
      { ...fields.location, ...coordinates.value, isAddressHidden: fields.isAddressHidden },
      portfolioId,
    ),
  );
}

// Populated by the row's id, not the object: a background read of the record hands out a fresh
// object with the same id, and that must not wipe what is being typed
watch(
  () => holder.details?.location?.id,
  () => {
    const location = holder.details?.location;

    onRecord.value = location;
    coordinates.value = location ? { latitude: location.latitude, longitude: location.longitude } : undefined;
    Object.assign(form.fields, { location: location ?? null, isAddressHidden: location?.isAddressHidden || false });
  },
  { immediate: true },
);

defineExpose({ save, form, isBusy: isLoading });
</script>
